/**
 * WorkflowReportPreview - PDF-like preview of workflow report content
 *
 * Renders all sections with markdown formatting in a print-friendly layout.
 * Used for:
 * - "View Preview" button in ReportEditorV2 (analysts/admins)
 * - Client and Committee review views
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportWorkflow } from '../../../../../services/firestore';
import { useRatedInstitutions } from '../../../../../hooks/useInstitutions';
import MarkdownWithAssets from '../../../../../components/report/MarkdownWithAssets';
import useChartData from '../../../../../hooks/useChartData';
import './WorkflowReportPreview.css';

// 9 sections matching the editor – titles are i18n keys resolved at render
const REPORT_SECTIONS = [
  { id: 'activo', num: '01', titleKey: 'editorSections.activo' },
  { id: 'pasivo', num: '02', titleKey: 'editorSections.pasivo' },
  { id: 'patrimonio', num: '03', titleKey: 'editorSections.patrimonio' },
  { id: 'solvencia', num: '04', titleKey: 'editorSections.solvencia' },
  { id: 'liquidez', num: '05', titleKey: 'editorSections.liquidez' },
  { id: 'calidad_activos', num: '06', titleKey: 'editorSections.calidad_activos' },
  { id: 'eficiencia_gestion', num: '07', titleKey: 'editorSections.eficiencia_gestion' },
  { id: 'rentabilidad', num: '08', titleKey: 'editorSections.rentabilidad' },
  { id: 'riesgo_interes', num: '09', titleKey: 'editorSections.riesgo_interes' },
];

interface WorkflowReportPreviewProps {
  report: ReportWorkflow;
  sectionContent: Record<string, string>;
  onClose: () => void;
}

export default function WorkflowReportPreview({
  report,
  sectionContent,
  onClose,
}: WorkflowReportPreviewProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';

  // Fetch chart data for rendering embedded charts/tables
  const { data: chartData, isLoading: isChartDataLoading } = useChartData(
    report.institution_id,
    report.period
  );

  // Fetch institution data from BigQuery API
  const { institutions } = useRatedInstitutions();

  // Create a lookup map for institution names
  const institutionNameMap = useMemo(() => {
    const map = new Map<string, string>();
    institutions.forEach((inst) => map.set(inst.institution_id, inst.institution_name));
    return map;
  }, [institutions]);

  // Get institution name from BigQuery data
  const getInstitutionName = (institutionId: string): string => {
    return institutionNameMap.get(institutionId) || institutionId;
  };

  // Format period for display (e.g., "2024-03" → "Marzo 2024" / "March 2024")
  const formatPeriod = (period: string): string => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  };

  // Handle print - add class to body to hide editor during print
  const handlePrint = () => {
    document.body.classList.add('printing-preview');
    window.print();
    // Remove class after print dialog closes
    setTimeout(() => {
      document.body.classList.remove('printing-preview');
    }, 100);
  };

  return (
    <div className="preview-overlay">
      {/* Toolbar - hidden when printing */}
      <div className="preview-toolbar no-print">
        <div className="toolbar-left">
          <button className="toolbar-btn" onClick={onClose}>
            ← {t('reportPreview.backToEditor')}
          </button>
        </div>
        <div className="toolbar-center">
          <h2>{t('reportPreview.title')}</h2>
        </div>
        <div className="toolbar-right">
          <button className="toolbar-btn" onClick={handlePrint}>
            🖨️ {t('reportPreview.print')}
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div className="preview-container">
        <div className="preview-content">
          {/* Report Header */}
          <div className="report-header">
            <h1 className="report-title">{t('reportPreview.reportTitle')}</h1>
            <div className="report-metadata">
              <p><strong>{t('reportPreview.institution')}</strong> {getInstitutionName(report.institution_id)}</p>
              <p><strong>{t('reportPreview.period')}</strong> {formatPeriod(report.period)}</p>
              <p><strong>{t('reportPreview.date')}</strong> {new Date().toLocaleDateString(locale)}</p>
            </div>
          </div>

          {/* All Sections */}
          <div className="report-sections">
            {REPORT_SECTIONS.map((section) => {
              const content = sectionContent[section.id] || '';

              return (
                <section key={section.id} className="report-section">
                  <h2 className="section-title">
                    <span className="section-number">{section.num}</span>
                    {t(section.titleKey)}
                  </h2>
                  <div className="section-content">
                    {content ? (
                      <MarkdownWithAssets
                        content={content}
                        chartData={chartData}
                        isLoading={isChartDataLoading}
                      />
                    ) : (
                      <p className="no-content">{t('reportPreview.noContent')}</p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>

          {/* Report Footer */}
          <div className="report-footer">
            <p className="footer-disclaimer">
              {t('reportPreview.disclaimer')}
            </p>
            <p className="footer-timestamp">
              {t('reportPreview.generatedOn', { date: new Date().toLocaleString(locale) })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
